/**
 * Account-Based Marketing (ABM) Campaign Templates
 * Based on best practices from Demandbase, 6sense, Terminus, Engagio
 *
 * Key insights from ABM leaders:
 * - ABM delivers 208% higher revenue than traditional marketing (Terminus)
 * - Multi-threaded deals close 34% faster (Gong)
 * - Executive sponsorship increases deal size by 47%
 * - Personalization at the account level increases response by 3x
 * - Average enterprise deal involves 11+ stakeholders
 */

import { CampaignTemplate } from "./types";

export const ABM_EXECUTIVE_OUTREACH: CampaignTemplate = {
  id: "abm-executive-outreach",
  name: "Executive Outreach Campaign",
  category: "abm",
  description: "High-touch C-level outreach with strategic positioning.",
  strategy:
    "Based on Gong's analysis showing executive-sponsored deals are 4x more likely to close. Each email treats the executive as a peer, focuses on strategic outcomes rather than features, and offers exclusive access to thought leadership.",
  icon: "CrownOutlined",
  color: "#ec4899",
  targetAudience: "enterprise",
  personalizationLevel: "hyper",

  expectedOutcomes: [
    "10-20% response rate from C-level",
    "Executive sponsorship of evaluation",
    "Larger deal sizes",
    "Faster decision-making",
  ],

  bestPractices: [
    "Send from your own executive (CEO, CRO, VP)",
    "Research their public statements and priorities",
    "Focus on business outcomes, not product features",
    "Offer executive briefing, not demo",
    "Keep emails extremely short (under 100 words)",
  ],

  objectionHandling: [
    "'Delegate to team' → Offer to brief team and circle back with summary",
    "'Not a priority' → Reference industry trends making it urgent",
    "'Already have a solution' → Share competitive displacement case study",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event"],
    companySize: ["501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    jobTitles: ["CEO", "CTO", "CMO", "CRO", "CFO", "COO", "President", "Founder"],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "job_title",
      "industry",
    ],
  },

  emails: [
    {
      name: "Day 0 - Peer-to-Peer Intro",
      subject: "{{first_name}}, quick thought on {{strategic_topic}}",
      suggestedMjmlTemplateId: "coldOutreach-executiveIntro",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Peer-level introduction focused on strategic business outcome",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "strategic_topic",
          defaultValue: "customer experience transformation",
          description: "Strategic topic relevant to their role",
        },
        {
          key: "your_exec_name",
          defaultValue: "{{sender_name}}",
          description: "Your executive's name",
        },
        {
          key: "your_exec_title",
          defaultValue: "CEO",
          description: "Your executive's title",
        },
        {
          key: "strategic_observation",
          defaultValue: "I've been watching {{company_name}}'s expansion into new markets",
          description: "Personalized observation about their company",
        },
      ],
    },
    {
      name: "Day 5 - Industry Insight",
      subject: "What I'm seeing with {{industry}} leaders",
      suggestedMjmlTemplateId: "coldOutreach-industryInsight",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share exclusive industry insight positioning you as thought leader",
      variables: [
        {
          key: "industry_insight",
          defaultValue: "The top 10% of {{industry}} companies are investing heavily in AI customer engagement",
          description: "Relevant industry insight",
        },
        {
          key: "data_point",
          defaultValue: "seeing 40% better customer retention",
          description: "Supporting data",
        },
      ],
    },
    {
      name: "Day 11 - Peer Reference",
      subject: "What {{peer_exec}} at {{peer_company}} told me",
      suggestedMjmlTemplateId: "coldOutreach-peerReference",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Share insight from peer executive to build credibility",
      variables: [
        {
          key: "peer_exec",
          defaultValue: "the CMO",
          description: "Peer executive reference",
        },
        {
          key: "peer_company",
          defaultValue: "TechCorp",
          description: "Reference company name",
        },
        {
          key: "peer_insight",
          defaultValue: "They wished they had started this transformation 6 months earlier",
          description: "Insight from peer",
        },
      ],
    },
    {
      name: "Day 18 - Direct Value",
      subject: "Specific opportunity for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-specificOpportunity",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Present specific, researched opportunity for their company",
      variables: [
        {
          key: "specific_opportunity",
          defaultValue: "Based on your recent product launch, there's an opportunity to capture 30% more customer inquiries",
          description: "Tailored opportunity",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 15,
    dailyLimit: 45,
    requiresWarming: true,
    minDomainAge: 90,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Your executive's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "CEO",
      description: "Your executive's title",
      source: "manual",
    },
    {
      key: "sender_linkedin",
      defaultValue: "{{user_linkedin}}",
      description: "Executive's LinkedIn",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
    {
      key: "industry",
      defaultValue: "your industry",
      description: "Prospect's industry",
      source: "enrichment",
    },
  ],

  tags: ["abm", "executive", "c-level", "high-touch", "strategic"],
  bestFor: ["Enterprise Sales", "Strategic Accounts", "Large Deal Sizes"],
  difficulty: "advanced",
  setupTime: "30 minutes",
  inspiredBy: ["Gong", "Salesforce", "Challenger Sale"],
};

export const ABM_MULTI_STAKEHOLDER: CampaignTemplate = {
  id: "abm-multi-stakeholder",
  name: "Multi-Stakeholder Campaign",
  category: "abm",
  description: "Coordinate outreach across multiple stakeholders in an account.",
  strategy:
    "Based on Gong's research that multi-threaded deals close 34% faster. Target different stakeholders with tailored messaging that addresses their specific priorities while building consensus for your solution.",
  icon: "TeamOutlined",
  color: "#8b5cf6",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "Multiple conversations within account",
    "Faster consensus building",
    "Reduced single-threaded deal risk",
    "Better discovery of requirements",
  ],

  bestPractices: [
    "Map the buying committee before starting",
    "Customize messaging for each persona",
    "Coordinate timing to create 'buzz' in account",
    "Reference other stakeholders' interest carefully",
    "Identify and cultivate a champion",
  ],

  objectionHandling: [
    "'We're already evaluating' → Great! Would love to be part of the evaluation",
    "'Talk to [other person]' → Thank them, use intro, update your thread map",
    "'Not involved in this' → Ask who is, offer relevant content for their role",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["201-500", "501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    jobTitles: [
      "CEO",
      "CTO",
      "CMO",
      "VP",
      "Director",
      "Head of",
      "Manager",
    ],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "job_title",
    ],
  },

  emails: [
    {
      name: "Day 0 - Role-Specific Intro",
      subject: "{{first_name}}, quick question about {{their_area}}",
      suggestedMjmlTemplateId: "coldOutreach-roleSpecific",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Initial outreach tailored to their specific role and priorities",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "their_area",
          defaultValue: "customer experience strategy",
          description: "Their area of responsibility",
        },
        {
          key: "role_pain_point",
          defaultValue: "scaling support without scaling headcount",
          description: "Pain point specific to their role",
        },
        {
          key: "role_outcome",
          defaultValue: "reduce support costs while improving satisfaction",
          description: "Outcome they care about",
        },
      ],
    },
    {
      name: "Day 4 - Relevant Case Study",
      subject: "How {{role_peer}} at {{case_company}} solved this",
      suggestedMjmlTemplateId: "coldOutreach-roleCaseStudy",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share case study from someone in similar role",
      variables: [
        {
          key: "role_peer",
          defaultValue: "the VP of Customer Success",
          description: "Similar role at reference company",
        },
        {
          key: "case_company",
          defaultValue: "TechCorp",
          description: "Reference company",
        },
        {
          key: "case_result",
          defaultValue: "40% reduction in support tickets",
          description: "Result achieved",
        },
      ],
    },
    {
      name: "Day 9 - Internal Interest",
      subject: "Your colleagues at {{company_name}} are interested",
      suggestedMjmlTemplateId: "coldOutreach-internalInterest",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Reference interest from other stakeholders (use carefully)",
      variables: [
        {
          key: "other_department",
          defaultValue: "marketing team",
          description: "Other team showing interest",
        },
        {
          key: "common_goal",
          defaultValue: "improving customer engagement",
          description: "Shared goal across teams",
        },
      ],
    },
    {
      name: "Day 14 - Consensus Builder",
      subject: "Bringing together {{company_name}}'s stakeholders",
      suggestedMjmlTemplateId: "coldOutreach-consensusBuilder",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Offer to facilitate cross-functional conversation",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 30,
    dailyLimit: 90,
    requiresWarming: true,
    minDomainAge: 60,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
    {
      key: "job_title",
      defaultValue: "{{prospect_title}}",
      description: "Prospect's job title",
      source: "enrichment",
    },
  ],

  tags: ["abm", "multi-stakeholder", "buying-committee", "enterprise", "consensus"],
  bestFor: ["Enterprise Sales", "Complex Sales", "Multi-Department Solutions"],
  difficulty: "advanced",
  setupTime: "35 minutes",
  inspiredBy: ["Gong", "6sense", "Demandbase"],
};

export const ABM_REFERRAL_INTRO: CampaignTemplate = {
  id: "abm-referral-intro",
  name: "Internal Referral Request",
  category: "abm",
  description: "Get introduced to decision makers through internal referrals.",
  strategy:
    "Based on research showing internally referred conversations have 5x higher success rates. Target accessible contacts who can introduce you to decision makers, providing value to both the referrer and the target.",
  icon: "ShareAltOutlined",
  color: "#06b6d4",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "Warm introduction to decision maker",
    "Internal champion development",
    "Higher meeting acceptance",
    "Better-qualified opportunities",
  ],

  bestPractices: [
    "Offer clear value to the referrer",
    "Make the ask easy and specific",
    "Provide a forward-friendly email",
    "Thank and update referrer on progress",
    "Don't ask strangers for referrals",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "event"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    jobTitles: [
      "Manager",
      "Senior",
      "Lead",
      "Specialist",
      "Coordinator",
      "Analyst",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Value-First Intro",
      subject: "Quick question, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-referralIntro",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Build rapport and offer value before asking for referral",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "value_for_them",
          defaultValue: "a resource that helps {{job_title}}s like you",
          description: "Value you're offering them",
        },
        {
          key: "their_expertise",
          defaultValue: "customer success",
          description: "Their area of expertise",
        },
      ],
    },
    {
      name: "Day 4 - Soft Referral Ask",
      subject: "Who handles {{target_area}} at {{company_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-softReferralAsk",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Ask who handles the area you're targeting",
      variables: [
        {
          key: "target_area",
          defaultValue: "customer engagement strategy",
          description: "Area you want to reach",
        },
        {
          key: "reason_for_asking",
          defaultValue: "I have some research that might be valuable for them",
          description: "Why you're asking",
        },
      ],
    },
    {
      name: "Day 9 - Forward-Friendly",
      subject: "Feel free to forward this, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-forwardFriendly",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Provide easy-to-forward message for their colleague",
      variables: [
        {
          key: "forward_message",
          defaultValue: "Hey [Name], thought this might be interesting for you...",
          description: "Pre-written forward text",
        },
        {
          key: "target_title",
          defaultValue: "VP of Marketing",
          description: "Title of person you want to reach",
        },
      ],
    },
    {
      name: "Day 14 - Direct Ask",
      subject: "Would you mind a quick intro?",
      suggestedMjmlTemplateId: "coldOutreach-directReferralAsk",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Direct ask for introduction with specific person",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "target_title",
      defaultValue: "VP of Marketing",
      description: "Title of person you want to reach",
      source: "manual",
    },
    {
      key: "target_area",
      defaultValue: "customer engagement",
      description: "Area you want to reach",
      source: "manual",
    },
  ],

  tags: ["abm", "referral", "internal-intro", "champion", "warm-intro"],
  bestFor: ["Enterprise Sales", "Complex Organizations", "New Account Entry"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Challenger Sale", "SPIN Selling", "Gap Selling"],
};

export const ABM_CHAMPION_BUILDING: CampaignTemplate = {
  id: "abm-champion-building",
  name: "Champion Building Campaign",
  category: "abm",
  description: "Develop internal champions who will advocate for your solution.",
  strategy:
    "Based on MEDDIC methodology's emphasis on identifying and enabling champions. Target mid-level influencers who benefit from your solution and equip them with the ammunition to sell internally.",
  icon: "TrophyOutlined",
  color: "#f59e0b",
  targetAudience: "enterprise",
  personalizationLevel: "high",

  expectedOutcomes: [
    "Internal advocate for your solution",
    "Insider knowledge of buying process",
    "Political support for deal",
    "Sustainable competitive advantage",
  ],

  bestPractices: [
    "Target people who personally benefit from your solution",
    "Provide ammunition they can use internally",
    "Make them look good to their leadership",
    "Share exclusive content and early access",
    "Keep them informed throughout the process",
  ],

  objectionHandling: [
    "'I don't have influence' → You understand the day-to-day better than anyone",
    "'Not my decision' → You can help us understand what matters to decision makers",
    "'What's in it for me?' → This can be a career-making initiative for you",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "event", "referral"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    jobTitles: [
      "Manager",
      "Senior Manager",
      "Director",
      "Lead",
      "Principal",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Recognition",
      subject: "{{first_name}}, I've been following your work",
      suggestedMjmlTemplateId: "coldOutreach-championRecognition",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Recognize their expertise and build relationship",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "recognition",
          defaultValue: "I saw your presentation on customer experience at the conference",
          description: "Specific recognition of their work",
        },
        {
          key: "shared_interest",
          defaultValue: "improving customer engagement",
          description: "Interest you share with them",
        },
      ],
    },
    {
      name: "Day 4 - Exclusive Content",
      subject: "Exclusive: {{content_title}}",
      suggestedMjmlTemplateId: "coldOutreach-exclusiveContent",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share exclusive content that makes them more knowledgeable",
      variables: [
        {
          key: "content_title",
          defaultValue: "2024 Customer Engagement Benchmark Report",
          description: "Exclusive content name",
        },
        {
          key: "content_benefit",
          defaultValue: "data you can use to benchmark your team's performance",
          description: "How it helps them",
        },
      ],
    },
    {
      name: "Day 9 - Career Benefit",
      subject: "This could be a big win for you, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-careerBenefit",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Connect solution success to their career advancement",
      variables: [
        {
          key: "career_benefit",
          defaultValue: "Driving this initiative could position you as the leader of digital transformation at {{company_name}}",
          description: "How it helps their career",
        },
      ],
    },
    {
      name: "Day 14 - Internal Pitch Help",
      subject: "I made this for your internal pitch",
      suggestedMjmlTemplateId: "coldOutreach-internalPitch",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Provide materials they can use to pitch internally",
      variables: [
        {
          key: "pitch_materials",
          defaultValue: "one-page business case and ROI calculator",
          description: "Materials you're providing",
        },
      ],
    },
    {
      name: "Day 20 - Champion Ask",
      subject: "Would you champion this, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-championAsk",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Direct ask to be their champion in the evaluation",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 30,
    dailyLimit: 90,
    requiresWarming: true,
    minDomainAge: 45,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
    {
      key: "job_title",
      defaultValue: "{{prospect_title}}",
      description: "Their job title",
      source: "enrichment",
    },
  ],

  tags: ["abm", "champion", "enablement", "internal-advocate", "meddic"],
  bestFor: ["Enterprise Sales", "Complex Sales Cycles", "Competitive Deals"],
  difficulty: "advanced",
  setupTime: "30 minutes",
  inspiredBy: ["MEDDIC", "Challenger Sale", "Command of the Message"],
};

export const ABM_ACCOUNT_WARMING: CampaignTemplate = {
  id: "abm-account-warming",
  name: "Account Warming Campaign",
  category: "abm",
  description: "Warm up strategic accounts before direct sales outreach.",
  strategy:
    "Based on Demandbase's ABM approach of warming accounts with valuable content before sales engages. Creates familiarity and credibility so when sales reaches out, prospects already recognize your brand.",
  icon: "FireOutlined",
  color: "#ef4444",
  targetAudience: "enterprise",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Brand recognition before sales call",
    "Higher response to sales outreach",
    "Educated prospects",
    "Shortened sales cycle",
  ],

  bestPractices: [
    "Focus on education, not selling",
    "Use display ads and LinkedIn alongside email",
    "Track engagement to signal sales readiness",
    "Coordinate with marketing on account signals",
    "Handoff to sales with full engagement history",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "purchased"],
    companySize: ["201-500", "501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Week 1 - Industry Insight",
      subject: "{{industry}} is changing fast, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-industryInsight",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Share industry insight without mentioning your product",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "industry_insight",
          defaultValue: "AI is reshaping how companies engage customers",
          description: "Industry trend to share",
        },
        {
          key: "insight_source",
          defaultValue: "Gartner",
          description: "Source of insight",
        },
      ],
    },
    {
      name: "Week 2 - Research Report",
      subject: "Free research: {{research_title}}",
      suggestedMjmlTemplateId: "coldOutreach-researchReport",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Offer valuable ungated research",
      variables: [
        {
          key: "research_title",
          defaultValue: "2024 State of Customer Engagement",
          description: "Research report name",
        },
      ],
    },
    {
      name: "Week 3 - Case Study",
      subject: "How {{case_study_company}} transformed their customer experience",
      suggestedMjmlTemplateId: "coldOutreach-warmingCaseStudy",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Share relevant case study without hard sell",
    },
    {
      name: "Week 4 - Expert Interview",
      subject: "Interview: {{expert_name}} on {{topic}}",
      suggestedMjmlTemplateId: "coldOutreach-expertInterview",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Share thought leadership content",
      variables: [
        {
          key: "expert_name",
          defaultValue: "Industry Expert",
          description: "Expert featured",
        },
        {
          key: "topic",
          defaultValue: "the future of customer engagement",
          description: "Topic covered",
        },
      ],
    },
    {
      name: "Week 6 - Sales Handoff",
      subject: "{{first_name}}, quick question",
      suggestedMjmlTemplateId: "coldOutreach-salesHandoff",
      delayAfterPrevious: { value: 14, unit: "days" },
      purpose: "Transition to sales conversation",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Marketing/SDR name",
      source: "crm",
    },
    {
      key: "industry",
      defaultValue: "Your industry",
      description: "Prospect's industry",
      source: "enrichment",
    },
    {
      key: "case_study_company",
      defaultValue: "Leading Company",
      description: "Reference customer",
      source: "manual",
    },
  ],

  tags: ["abm", "warming", "nurture", "pre-sales", "content"],
  bestFor: ["ABM Programs", "Enterprise Marketing", "Long Sales Cycles"],
  difficulty: "intermediate",
  setupTime: "25 minutes",
  inspiredBy: ["Demandbase", "6sense", "Terminus"],
};

export const ABM_CUSTOM_SOLUTION: CampaignTemplate = {
  id: "abm-custom-solution",
  name: "Custom Solution Proposal",
  category: "abm",
  description:
    "Present a tailored solution proposal to strategic accounts.",
  strategy:
    "Based on enterprise sales methodology of leading with a custom proposal rather than generic capabilities. Show you've done the work to understand their specific situation and have a tailored solution ready.",
  icon: "SolutionOutlined",
  color: "#10b981",
  targetAudience: "enterprise",
  personalizationLevel: "hyper",

  expectedOutcomes: [
    "Higher perceived value",
    "Differentiation from competitors",
    "Faster evaluation process",
    "Executive attention",
  ],

  bestPractices: [
    "Research the account thoroughly before creating proposal",
    "Reference their specific challenges and goals",
    "Include specific ROI projections for their situation",
    "Offer multiple engagement options",
    "Follow up with executive summary for C-level",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event"],
    companySize: ["501-1000", "1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing"],
    jobTitles: [
      "VP",
      "Director",
      "C-Level",
      "Head of",
    ],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "job_title",
      "industry",
    ],
  },

  emails: [
    {
      name: "Day 0 - Custom Proposal",
      subject: "Custom proposal for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-customProposal",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Present tailored solution proposal",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "proposal_title",
          defaultValue: "Customer Engagement Transformation for {{company_name}}",
          description: "Custom proposal name",
        },
        {
          key: "key_challenge_1",
          defaultValue: "scaling customer support without adding headcount",
          description: "Their first challenge",
        },
        {
          key: "key_challenge_2",
          defaultValue: "improving customer satisfaction scores",
          description: "Their second challenge",
        },
        {
          key: "proposed_outcome",
          defaultValue: "40% reduction in support tickets with 15% higher CSAT",
          description: "Projected outcome",
        },
        {
          key: "proposal_url",
          defaultValue: "{{asset_url}}",
          description: "Link to proposal document",
        },
      ],
    },
    {
      name: "Day 4 - ROI Deep Dive",
      subject: "The numbers behind our proposal, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-roiDeepDive",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share detailed ROI analysis specific to their situation",
      variables: [
        {
          key: "roi_estimate",
          defaultValue: "287% ROI in first year",
          description: "Estimated ROI",
        },
        {
          key: "payback_period",
          defaultValue: "4.2 months",
          description: "Estimated payback",
        },
      ],
    },
    {
      name: "Day 9 - Executive Summary",
      subject: "Executive summary: {{company_name}} proposal",
      suggestedMjmlTemplateId: "coldOutreach-executiveSummary",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share brief executive summary for sharing upward",
      variables: [
        {
          key: "executive_summary",
          defaultValue: "One-page overview of the opportunity and recommended approach",
          description: "Executive summary description",
        },
      ],
    },
    {
      name: "Day 14 - Discussion",
      subject: "Discuss the proposal, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-proposalDiscussion",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Request meeting to discuss proposal",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 20,
    dailyLimit: 60,
    requiresWarming: true,
    minDomainAge: 60,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Account executive name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "{{user_title}}",
      description: "Account executive title",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
    {
      key: "proposal_url",
      defaultValue: "{{asset_url}}",
      description: "Link to proposal",
      source: "manual",
    },
  ],

  tags: ["abm", "custom-proposal", "enterprise", "strategic", "roi"],
  bestFor: ["Strategic Accounts", "Enterprise Sales", "High-Value Deals"],
  difficulty: "advanced",
  setupTime: "40 minutes",
  inspiredBy: ["MEDDIC", "Command of the Message", "Sandler"],
};

export const ABM_TEMPLATES = [
  ABM_EXECUTIVE_OUTREACH,
  ABM_MULTI_STAKEHOLDER,
  ABM_REFERRAL_INTRO,
  ABM_CHAMPION_BUILDING,
  ABM_ACCOUNT_WARMING,
  ABM_CUSTOM_SOLUTION,
];
